import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { apiService } from './services/api.service';
import { ThemeProvider } from './contexts/ThemeContext';
import DarkModeToggle from './components/DarkModeToggle';
import Login from './components/Login';
import ApiExplorer from './components/ApiExplorer';
import SupplierInfo from './components/SupplierInfo';
import SupplierSwitcher from './components/SupplierSwitcher';
import TableViewerPage from './pages/TableViewerPage';

import './App.css';
import './styles/darkmode.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentEnvironment, setCurrentEnvironment] = useState(apiService.getCurrentEnvironment());
  const [userName, setUserName] = useState('');

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const supplierName = localStorage.getItem('excel_supplier_name');
      const userEmail = localStorage.getItem('user_email');
      
      if (supplierName) {
        // Test API connection with fixed credentials
        await apiService.healthCheck();
        setIsAuthenticated(true);
        if (userEmail) {
          setUserName(userEmail);
        }
      }
    } catch (error) {
      apiService.clearSession();
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (email: string, supplier?: string) => {
    try {
      // Set supplier for user session (Google OAuth handles user auth)
      if (supplier) {
        apiService.setSupplier(supplier);
      }
      // Store user email for display
      localStorage.setItem('user_email', email);
      setUserName(email);
      
      await apiService.healthCheck();
      setIsAuthenticated(true);
      // Update environment state after login
      setCurrentEnvironment(apiService.getCurrentEnvironment());
      return true;
    } catch (error) {
      apiService.clearSession();
      throw error;
    }
  };

  const handleLogout = () => {
    apiService.clearSession();
    localStorage.removeItem('user_email');
    setIsAuthenticated(false);
    setUserName('');
  };

  const handleSupplierChange = (supplierName: string) => {
    // The SupplierSwitcher component handles the actual supplier change
    // This callback can be used for additional logic if needed
    setCurrentEnvironment(apiService.getCurrentEnvironment());
  };

  if (loading) {
    return null; // No loading indicator
  }

  const AppContent = () => {
    const location = useLocation();
    const isTableViewerPage = location.pathname === '/table-viewer';

    return (
      <div className="App">
        {isAuthenticated && !isTableViewerPage && (
          <nav className="navbar">
            <div className="nav-brand">              
              <SupplierSwitcher onSupplierChange={handleSupplierChange} />              
              <div className="supplier-info">
                <SupplierInfo />
              </div>
              {/* <h1>� Reports Explorer</h1> */}
              <div className="environment-badge">
                <span className={`env-indicator env-${currentEnvironment}`}>
                  {currentEnvironment.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="nav-center">
              <div className="nav-links">
                {/* Navigation removed - Reports Explorer is the main page */}
              </div>
            </div>
            <div className="nav-right">
              {/* <div className="supplier-info">
                <SupplierInfo />
              </div> */}
              <DarkModeToggle />
              <div className="user-welcome">
                <span className="welcome-text">
                  Welcome, {userName.split('@')[0].replace(/\./g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </span>
              </div>
              <button onClick={handleLogout} className="logout-btn">
                Logout
              </button>
            </div>
          </nav>
        )}

        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <ApiExplorer />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/table-viewer"
            element={<TableViewerPage />}
          />

        </Routes>
      </div>
    );
  };

  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
